// ═══ useTableProfile — persistencia por perfil de utilizador via GenAD (v2.2.0) ═══
// CANONICO (MANUAIS/08.8.TABLES.md §Persistencia). Uso:
//
//   var profile = useTableProfile('GenHUMANS', 'employees_list');
//   <DataTable ... layoutConfig={profile.layoutConfig} onLayoutChange={profile.saveLayout} />
//
// - GET  {base}/profile?app=&page=&feature=DataTable  (no mount; falha → null = defaults)
// - POST {base}/profile  {app, page, feature, config} (fire-and-forget; erro silencioso)
// - SEM localStorage como fonte do perfil — o perfil vive no GenAD (multi-dispositivo).
// - Zero dependencias (fetch nativo + React).
//
// TOKENS (v2.2.0 — AUTO-RENOVACAO): o access token GenAD expira em 9h e cada app
// guarda os seus tokens sob chaves proprias (algumas emitem ate tokens locais que
// o GenAD rejeita). O hook:
//   1. tenta os candidatos (genad_access_token > {app}_token > accessToken);
//   2. se TODOS dao 401 e existe genad_refresh_token (valido 7 dias), renova via
//      POST {base}/auth/refresh e grava os novos tokens nas chaves oficiais;
//   3. repete o pedido com o token renovado.
// O token funcional fica em cache no modulo (sem re-probes por pedido).
var GENAD_PROFILE_BASE = 'https://genad.generis.pt/backend/api/v1';

import React from 'react';

// cache do token que o GenAD aceita (partilhado por todas as instancias do hook)
var workingToken = null;

function readLocalStorage(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
}
function writeLocalStorage(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
}

function tokenCandidates(appKey) {
    var out = [];
    var keys = ['genad_access_token', String(appKey || '').toLowerCase() + '_token', 'accessToken'];
    for (var i = 0; i < keys.length; i++) {
        var t = readLocalStorage(keys[i]);
        if (t && out.indexOf(t) === -1) out.push(t);
    }
    if (workingToken && out.indexOf(workingToken) === -1) out.unshift(workingToken);
    return out;
}

// Renova via refresh token. Devolve o novo accessToken ou null.
function refreshAccessToken(base) {
    var rt = readLocalStorage('genad_refresh_token');
    if (!rt) return Promise.resolve(null);
    return fetch(base + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken: rt })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (res) {
        if (!res || !res.success || !res.accessToken) return null;
        writeLocalStorage('genad_access_token', res.accessToken);
        if (res.refreshToken) writeLocalStorage('genad_refresh_token', res.refreshToken);
        workingToken = res.accessToken;
        return res.accessToken;
    }).catch(function () { return null; });
}

// fetch com auth resiliente: candidatos -> 401 em todos -> refresh -> retry.
// Devolve Response ou null (falha total). Nunca lanca.
function authFetch(url, options, appKey, base) {
    var tokens = tokenCandidates(appKey);
    function attemptWith(token) {
        var opts = Object.assign({}, options || {});
        opts.headers = Object.assign({ Authorization: 'Bearer ' + token }, options && options.headers);
        return fetch(url, opts);
    }
    function tryNext(idx) {
        if (idx >= tokens.length) {
            // todos os candidatos falharam (ou lista vazia) -> renovar e retry 1x
            return refreshAccessToken(base).then(function (fresh) {
                if (!fresh) return null;
                return attemptWith(fresh).catch(function () { return null; });
            });
        }
        return attemptWith(tokens[idx]).then(function (r) {
            if (r.status === 401) return tryNext(idx + 1);
            workingToken = tokens[idx];
            return r;
        }).catch(function () { return tryNext(idx + 1); });
    }
    if (!tokens.length) {
        return refreshAccessToken(base).then(function (fresh) {
            return fresh ? attemptWith(fresh).catch(function () { return null; }) : null;
        });
    }
    return tryNext(0);
}

export function useTableProfile(appKey, pageKey, opts) {
    var o = opts || {};
    var base = o.baseUrl || GENAD_PROFILE_BASE;

    var _s = React.useState(null);
    var layoutConfig = _s[0], setLayoutConfig = _s[1];
    var appRef = React.useRef(appKey);
    var pageRef = React.useRef(pageKey);
    appRef.current = appKey;
    pageRef.current = pageKey;

    // GET do perfil no mount (e quando muda a page). Auth falhada/servico fora ->
    // layoutConfig null -> a DataTable usa os defaults da app (binario, sem fallbacks).
    // v2.2.1: resultado loggado na consola (debug de producao).
    React.useEffect(function () {
        var alive = true;
        var url = base + '/profile?app=' + encodeURIComponent(appKey) + '&page=' + encodeURIComponent(pageKey) + '&feature=DataTable';
        authFetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } }, appKey, base)
            .then(function (r) {
                if (typeof console !== 'undefined' && console.info && alive) {
                    console.info('[useTableProfile] GET ' + pageKey + ' →', r ? ('HTTP ' + r.status) : 'sem resposta');
                }
                return r && r.ok ? r.json() : null;
            })
            .then(function (res) {
                if (!alive) return;
                var cfg = res && res.data && typeof res.data.config === 'object' ? res.data.config : null;
                setLayoutConfig(cfg);
            })
            .catch(function () { if (alive) setLayoutConfig(null); });
        return function () { alive = false; };
    }, [appKey, pageKey, base]);

    // POST fire-and-forget: gravacao falhada NUNCA parte a UI (silenciosa).
    // POST fire-and-forget: gravacao falhada NUNCA parte a UI (silenciosa).
    // v2.2.1: os falhanos de rede/HTTP sao loggados na consola (debug de producao).
    var saveLayout = React.useCallback(function (config) {
        var body = JSON.stringify({ app: appRef.current, page: pageRef.current, feature: 'DataTable', config: config });
        authFetch(base + '/profile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: body
        }, appRef.current, base).then(function (r) {
            if (!r || !r.ok) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('[useTableProfile] POST /profile falhou:', r ? ('HTTP ' + r.status) : 'sem resposta (rede/CORS)', '— perfil NAO gravado (' + pageRef.current + ')');
                }
            }
        });
    }, [base]);

    return { layoutConfig: layoutConfig, saveLayout: saveLayout };
}

export default useTableProfile;
